import { useCallback, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GppBadIcon from '@mui/icons-material/GppBad';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import CopyToClipboardButton from '../../atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import PaymentCommand from '../../services/command/payment/PaymentCommand.services';
import { ProcessMobileExpressOrdersResponse } from '../../types/models/payment/ProcessMobileExpressOrders.types';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { MEF } from './mobileExpressFraud.classes';

const MAX_ORDER_COUNT = 100;

const parseOrderNumbers = (input: string): string[] =>
    input
        .split(/[\n,]/)
        .map((value) => value.trim())
        .filter((value) => value !== '');

export default function MobileExpressFraud() {
    const { t } = useTranslation();
    const [orderNumbersInput, setOrderNumbersInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ProcessMobileExpressOrdersResponse | null>(null);

    const orderNumbers = useMemo(() => parseOrderNumbers(orderNumbersInput), [orderNumbersInput]);
    const isOverLimit = orderNumbers.length > MAX_ORDER_COUNT;

    const handleClear = useCallback(() => {
        setOrderNumbersInput('');
        setResult(null);
    }, []);

    const handleProcess = useCallback(async () => {
        if (orderNumbers.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('mobileExpressFraud.emptyInput'),
                confirmButtonText: t('general.accept'),
            });
            return;
        }

        if (isOverLimit) {
            await Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('mobileExpressFraud.maxOrderLimitExceeded', { max: MAX_ORDER_COUNT }),
                confirmButtonText: t('general.accept'),
            });
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const response = await PaymentCommand.ProcessMobileExpressOrders({ orderNumbers });

            if (!response.isValidRequest) {
                await Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: response.errorMessage || t('general.failed'),
                    confirmButtonText: t('general.accept'),
                });
                return;
            }

            setResult(response);

            if (response.operationStatus) {
                await Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('mobileExpressFraud.processSuccess'),
                    confirmButtonText: t('general.accept'),
                });
            }
        } catch (error) {
            console.error('Error during ProcessMobileExpressOrders:', error);
        } finally {
            setLoading(false);
        }
    }, [isOverLimit, orderNumbers, t]);

    const statItems = useMemo(
        () =>
            result
                ? [
                      { label: t('mobileExpressFraud.totalOrders'), value: result.totalOrders, color: 'primary' as const },
                      {
                          label: t('mobileExpressFraud.matchedOrderCount'),
                          value: result.matchedOrderCount,
                          color: 'success' as const,
                      },
                      {
                          label: t('mobileExpressFraud.unmatchedOrderCount'),
                          value: result.unmatchedOrderCount,
                          color: 'error' as const,
                      },
                      {
                          label: t('mobileExpressFraud.uniqueOrderCount'),
                          value: result.uniqueOrderCount,
                          color: 'info' as const,
                      },
                      {
                          label: t('mobileExpressFraud.totalCustomerCount'),
                          value: result.totalCustomerCount,
                          color: 'default' as const,
                      },
                      {
                          label: t('mobileExpressFraud.uniqueCustomerCount'),
                          value: result.uniqueCustomerCount,
                          color: 'default' as const,
                      },
                      {
                          label: t('mobileExpressFraud.customerServiceSuccessCount'),
                          value: result.customerServiceSuccessCount,
                          color: 'success' as const,
                      },
                      {
                          label: t('mobileExpressFraud.customerServiceAlreadyResetCount'),
                          value: result.customerServiceAlreadyResetCount,
                          color: 'warning' as const,
                      },
                      {
                          label: t('mobileExpressFraud.customerServiceFailedChunkCount'),
                          value: result.customerServiceFailedChunkCount,
                          color: 'error' as const,
                      },
                  ]
                : [],
        [result, t]
    );

    return (
        <SCUIContent className={MEF.page}>
            <Grid container spacing={2}>
                <Grid className={MEF.breadcrumbSection} size={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <GppBadIcon color="error" fontSize="large" />
                        <SCUIBreadCrumb
                            title={t('mobileExpressFraud.pageTitle')}
                            componentLink={PageUrl.mobileExpressFraud.url}
                            style={{ cursor: 'pointer' }}
                        />
                    </Box>
                </Grid>

                <Grid className={MEF.loadingSection} size={12}>
                    {loading && <SCUILoadingLinear display={loading} />}
                </Grid>

                <Grid className={MEF.mainLayout} container spacing={3} size={12}>
                    <Grid
                        className={MEF.inputSection}
                        size={{
                            xs: 12,
                            md: 5,
                            lg: 4,
                        }}
                    >
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('mobileExpressFraud.inputTitle')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('mobileExpressFraud.inputDescription')}
                                </Typography>
                                <TextField
                                    className={MEF.inputTextOrderNumbers}
                                    label={t('mobileExpressFraud.orderNumbersLabel')}
                                    placeholder={t('mobileExpressFraud.orderNumbersPlaceholder')}
                                    multiline
                                    rows={12}
                                    fullWidth
                                    value={orderNumbersInput}
                                    error={isOverLimit}
                                    onChange={(event) => setOrderNumbersInput(event.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <Box
                                    className={MEF.hintText}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mt: 1,
                                    }}
                                >
                                    <Typography variant="caption" color={isOverLimit ? 'error' : 'text.secondary'}>
                                        {t('mobileExpressFraud.orderCountHint', {
                                            count: orderNumbers.length,
                                            max: MAX_ORDER_COUNT,
                                        })}
                                    </Typography>
                                    {isOverLimit && (
                                        <Chip
                                            size="small"
                                            color="error"
                                            label={t('mobileExpressFraud.limitExceeded')}
                                        />
                                    )}
                                </Box>
                                <Box className={MEF.actionsRow} sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Box className={MEF.clearButtonWrap}>
                                        <SCUIButton
                                            className={MEF.inputButtonClear}
                                            text={t('general.clear')}
                                            type="error"
                                            buttonType="button"
                                            sizeType="default"
                                            size="m"
                                            style={{ minWidth: 120, borderRadius: '4px' }}
                                            startIcon={<DeleteIcon sx={{ color: '#ffffff' }} />}
                                            onClick={handleClear}
                                            disabled={loading}
                                        />
                                    </Box>
                                    <Box className={MEF.processButtonWrap}>
                                        <SCUIButton
                                            className={MEF.inputButtonProcess}
                                            text={t('mobileExpressFraud.processButton')}
                                            type="primary"
                                            buttonType="button"
                                            sizeType="default"
                                            size="m"
                                            style={{ minWidth: 120, borderRadius: '4px' }}
                                            startIcon={<PlayArrowIcon sx={{ color: '#ffffff' }} />}
                                            onClick={handleProcess}
                                            disabled={loading}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        className={MEF.resultsSection}
                        size={{
                            xs: 12,
                            md: 7,
                            lg: 8,
                        }}
                    >
                        {result ? (
                            <Card className={MEF.resultsCard} variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t('mobileExpressFraud.resultsTitle')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('mobileExpressFraud.correlationId')}: {result.correlationId}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid className={MEF.statGrid} container spacing={2}>
                                        {statItems.map((item) => (
                                            <Grid
                                                key={item.label}
                                                className={MEF.statItem}
                                                size={{
                                                    xs: 6,
                                                    sm: 4,
                                                    md: 4,
                                                }}
                                            >
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 1.5,
                                                        borderColor:
                                                            item.color === 'default' ? 'divider' : `${item.color}.light`,
                                                    }}
                                                >
                                                    <Typography variant="h5" color={`${item.color}.main`}>
                                                        {item.value}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.label}
                                                    </Typography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {result.unmatchedOrderNumbers.length > 0 && (
                                        <Card
                                            className={MEF.unmatchedCard}
                                            variant="outlined"
                                            sx={{ mt: 3, borderColor: 'error.light' }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Typography variant="subtitle1" color="error">
                                                        {t('mobileExpressFraud.unmatchedOrdersTitle')} (
                                                        {result.unmatchedOrderCount})
                                                    </Typography>
                                                    <CopyToClipboardButton
                                                        textToCopy={result.unmatchedOrderNumbers.join(', ')}
                                                    />
                                                </Box>
                                                <Box
                                                    className={MEF.unmatchedListWrap}
                                                    sx={{ maxHeight: 220, overflowY: 'auto' }}
                                                >
                                                    <List className={MEF.unmatchedList} dense>
                                                        {result.unmatchedOrderNumbers.map((orderNumber) => (
                                                            <ListItem
                                                                className={MEF.unmatchedListItem}
                                                                key={orderNumber}
                                                                divider
                                                            >
                                                                <ListItemText primary={orderNumber} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Card variant="outlined" sx={{ height: '100%', minHeight: 320 }}>
                                <CardContent
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <GppBadIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                    <Typography variant="h6" color="text.secondary">
                                        {t('mobileExpressFraud.noResultsTitle')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('mobileExpressFraud.noResultsDescription')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </SCUIContent>
    );
}
